import Card from '../../../../components/Card'
import cx from 'classnames'
import styles from './DefineAreas.module.scss'
import Input from '../../../../components/Input'
import { fabric } from 'fabric'
import Button from '../../../../components/Button'
import Select, { SelectOption } from '../../../../components/Select'
import Heading from '../../../../components/Heading'
import Checkbox from '../../../../components/Checkbox'
import React, { useCallback, useMemo, useState } from 'react'
import { useIndividualizationContext } from '../../../../contexts/individualizationContext'
import { useTranslation } from 'react-i18next'
import { faMinus, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import IconButton from '../../../../components/IconButton'
import CircleColorPicker from '../../../../components/CircleColorPicker'
import MultiSelect from '../../../../components/MultiSelect'
import CategoryMediaSelect from '../../../Settings/MediaSettings/CategoryMediaSelect/CategoryMediaSelect'

type ActiveSpotProps = {
    activeSpot: fabric.Object
    addEdge: (id: string | number) => void
    removeEdge: (id: string | number) => void
    removeSpot: (id: string | number) => void
    changeExampleText: (id: string | number, data: Partial<fabric.Text>) => void
    exampleTexts: fabric.Object[]
}

const ActiveSpotCard: React.FC<ActiveSpotProps> = ({
    activeSpot,
    addEdge,
    removeEdge,
    removeSpot,
    changeExampleText,
    exampleTexts,
}) => {
    const {
        updateSpotData,
        updateSpot,
        spots,
        fonts,
        measurements,
        activeSpotId,
    } = useIndividualizationContext()
    const { t } = useTranslation(['individualization'])
    const [isMediaCategoryFiltered, setIsMediaCategoryFiltered] = useState(
        activeSpot.data.media_category_filtered ?? false
    )

    const getOptionsForActive: SelectOption[] = useMemo(() => {
        const elements = [
            { label: t('active_spot_cart.single_text'), value: 'text' },
            { label: t('active_spot_cart.single_image'), value: 'image' },
            {
                label: t('active_spot_cart.cutting_mask'),
                value: 'cutting mask',
            },
        ]

        if (activeSpot instanceof fabric.Rect) {
            elements.push({
                label: t('active_spot_cart.auto_text'),
                value: 'auto text',
            })
        }

        return elements
    }, [t, activeSpot])

    const realSizes: { width: string; height: string } | null = useMemo(() => {
        const spot = spots.find((s) => s.data.id === activeSpotId)

        if (!spot) {
            return null
        }

        const template_id = spot.data.template_id
        if (!template_id || measurements.length === 0) {
            return null
        }

        const measurement = measurements.find(
            (m) => m.line.data.template_id === template_id
        )

        if (
            !measurement ||
            !measurement.line ||
            !measurement.line.data ||
            !measurement.line.data.orientation ||
            !measurement.line.data.real_length
        ) {
            return null
        }

        const orientation: 'vertical' | 'horizontal' =
            measurement.line.data.orientation
        const measured = parseFloat(measurement.line.data.real_length)
        const size =
            orientation === 'horizontal'
                ? measurement.line.getScaledWidth()
                : measurement.line.getScaledHeight()

        const ratio = measured / size

        return {
            width: (spot.getScaledWidth() * ratio).toFixed(2),
            height: (spot.getScaledHeight() * ratio).toFixed(2),
        }
    }, [measurements, spots, activeSpotId])

    const getMediaOptions = useMemo(() => {
        return [
            {
                label: t('active_spot_cart.labels.media_upload'),
                value: 'media_upload',
            },
            {
                label: t('active_spot_cart.labels.media_library'),
                value: 'media_library',
            },
            {
                label: t('active_spot_cart.labels.library_upload'),
                value: 'library_upload',
            },
        ]
    }, [t])

    const mediaOptionValue = useCallback(() => {
        if (activeSpot.data.media_upload && !activeSpot.data.media_library)
            return 'media_upload'
        if (activeSpot.data.media_library && !activeSpot.data.media_upload)
            return 'media_library'
        return 'library_upload'
    }, [activeSpot])

    const getOptionsForSync: SelectOption[] = useMemo(() => {
        const all = spots
            .filter((spot) => {
                return (
                    spot.data.id !== activeSpot.data.id &&
                    spot.data.template_id === activeSpot.data.template_id &&
                    spot.data.type &&
                    (spot.data.type === 'text' ||
                        spot.data.type === 'auto text')
                )
            })
            .map((spot) => ({
                label: spot.data.name,
                value: spot.data.id,
            }))

        return [
            ...all,
            { label: t('active_spot_cart.labels.no_sync'), value: '0' },
        ]
    }, [spots, activeSpot.data.template_id, activeSpot.data.id, t])

    const handleMediaSelect = (value: string) => {
        switch (value) {
            case 'media_upload':
                updateSpotData(activeSpot.data.id, 'media_upload', true)
                updateSpotData(activeSpot.data.id, 'media_library', false)
                break
            case 'media_library':
                updateSpotData(activeSpot.data.id, 'media_upload', false)
                updateSpotData(activeSpot.data.id, 'media_library', true)
                break
            case 'library_upload':
                updateSpotData(activeSpot.data.id, 'media_upload', true)
                updateSpotData(activeSpot.data.id, 'media_library', true)
                break
            default:
                break
        }
    }

    const handleToggleCategory = (categoryId: number) => {
        const existingCategories: number[] =
            activeSpot.data.media_category_ids || []
        let updatedCategories: number[]

        if (existingCategories.includes(categoryId)) {
            updatedCategories = existingCategories.filter(
                (id: number) => id !== categoryId
            )
        } else {
            updatedCategories = [
                ...new Set([...existingCategories, categoryId]),
            ]
        }

        updateSpotData(
            activeSpot.data.id,
            'media_category_ids',
            updatedCategories
        )
    }

    const getExampleText = useMemo(() => {
        const element = exampleTexts.find(
            (o) => o.data.spot_id === activeSpot.data.id
        )

        if (element) {
            return element as fabric.Text
        } else {
            return null
        }
    }, [exampleTexts, activeSpot])
    return (
        <Card key={activeSpot.data.id} classes={'zr-my-4'}>
            <IconButton
                icon={faTrashAlt}
                classes={cx(styles['card-icon'])}
                color={'#2f2f2f'}
                style={{ zIndex: 200 }}
                onClick={() => removeSpot(activeSpot.data.id)}
            />
            <Input
                type={'text'}
                placeholder={t('active_spot_cart.labels.spot_name')}
                property={'name'}
                value={activeSpot.data.name ?? ''}
                onChange={(property, value) =>
                    updateSpotData(activeSpot.data.id, property, value)
                }
                classes={'zr-mt-6'}
                multiLanguage={true}
                multiLanguageValue={activeSpot.data.name_multi}
                onMultiLanguageChange={(property, value) =>
                    updateSpotData(activeSpot.data.id, property, value)
                }
            />
            <Input
                type={'text'}
                placeholder={t('active_spot_cart.labels.hint')}
                property={'hint'}
                value={activeSpot.data.hint ?? ''}
                onChange={(property, value) =>
                    updateSpotData(activeSpot.data.id, property, value)
                }
                multiLanguage={true}
                classes={'zr-mt-4'}
                multiLanguageValue={activeSpot.data.hint_multi}
                onMultiLanguageChange={(property, value) =>
                    updateSpotData(activeSpot.data.id, property, value)
                }
            />
            {!(activeSpot instanceof fabric.Polygon) && (
                <div className={cx(styles['three-input-row'], 'zr-my-4')}>
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.x_position')}
                        property={'left'}
                        value={activeSpot.getCenterPoint().x ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.y_position')}
                        property={'top'}
                        value={activeSpot.getCenterPoint().y ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.angle')}
                        property={'angle'}
                        value={activeSpot.angle ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                </div>
            )}
            {activeSpot instanceof fabric.Rect && (
                <div className={cx(styles['two-input-row'], 'zr-mt-4')}>
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.height')}
                        property={'height'}
                        value={activeSpot.height ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.width')}
                        property={'width'}
                        value={activeSpot.width ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                </div>
            )}
            {activeSpot instanceof fabric.Circle && (
                <div className={cx(styles['one-input-row'], 'zr-mt-4')}>
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.radius')}
                        property={'radius'}
                        value={activeSpot.radius ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                </div>
            )}
            {activeSpot instanceof fabric.Ellipse && (
                <div className={cx(styles['two-input-row'], 'zr-mt-4')}>
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.x_radius')}
                        property={'rx'}
                        value={activeSpot.rx ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.y_radius')}
                        property={'ry'}
                        value={activeSpot.ry ?? ''}
                        onChange={(property, value) =>
                            updateSpot(activeSpot.data.id, property, value)
                        }
                    />
                </div>
            )}
            {activeSpot instanceof fabric.Polygon && (
                <div className={cx(styles['two-button-row'], 'zr-mt-4')}>
                    <Button
                        hierarchy={'secondary'}
                        text={t('active_spot_cart.labels.add_edge')}
                        fn={() => addEdge(activeSpot.data.id)}
                    />
                    <Button
                        hierarchy={'secondary'}
                        text={t('active_spot_cart.labels.remove_edge')}
                        fn={() => removeEdge(activeSpot.data.id)}
                    />
                </div>
            )}
            <div className={cx(styles['one-input-row'], 'zr-my-4')}>
                <Select
                    options={getOptionsForActive}
                    onSelect={(property, value) =>
                        updateSpotData(activeSpot.data.id, property, value)
                    }
                    property={'type'}
                    value={activeSpot.data.type ?? ''}
                    placeholder={t('active_spot_cart.labels.spot_type')}
                    searchable={false}
                />
            </div>
            {(activeSpot.data.type === 'image' ||
                activeSpot.data.type === 'text') && (
                <div className={cx(styles['one-input-row'], 'zr-my-4')}>
                    <Checkbox
                        property={'fixed_user_inputs'}
                        value={activeSpot.data.fixed_user_inputs ?? false}
                        label={t('active_spot_cart.labels.fixed_user_inputs')}
                        onChange={(property, value) => {
                            updateSpotData(activeSpot.data.id, property, value)
                        }}
                    />
                </div>
            )}
            {activeSpot.data.type === 'image' && (
                <>
                    <Heading level={4}>
                        {t('active_spot_cart.single_image')}
                    </Heading>
                    <React.Fragment>
                        <div className={'zr-mt-4'}>
                            <Select
                                options={getMediaOptions}
                                onSelect={(_, value) =>
                                    handleMediaSelect(value)
                                }
                                property={'media_option'}
                                value={mediaOptionValue()}
                                placeholder={t(
                                    'active_spot_cart.labels.media_options'
                                )}
                                searchable={false}
                            />
                        </div>
                        <p style={{ fontSize: '0.75rem', lineHeight: '1.5' }}>
                            {t('active_spot_cart.labels.individual_media_text')}
                        </p>
                        {'media_upload' in activeSpot.data &&
                            activeSpot.data.media_upload && (
                                <div className={'zr-my-4'}>
                                    <Checkbox
                                        property={'grayscale'}
                                        value={
                                            activeSpot.data.grayscale ?? false
                                        }
                                        label={t(
                                            'active_spot_cart.labels.grayscale'
                                        )}
                                        onChange={(property, value) => {
                                            updateSpotData(
                                                activeSpot.data.id,
                                                property,
                                                value
                                            )
                                        }}
                                    />
                                    <p
                                        className={cx(
                                            styles['small-text-line-height']
                                        )}
                                    >
                                        {t(
                                            'active_spot_cart.labels.gray_scale_text'
                                        )}
                                    </p>
                                </div>
                            )}

                        {'media_library' in activeSpot.data &&
                            activeSpot.data.media_library && (
                                <div className={'zr-my-4'}>
                                    <Checkbox
                                        property={'media_category_filtered'}
                                        value={
                                            activeSpot.data
                                                .media_category_filtered ??
                                            false
                                        }
                                        label={t(
                                            'active_spot_cart.labels.media_category_filtered'
                                        )}
                                        onChange={(property, value) => {
                                            setIsMediaCategoryFiltered(value)
                                            updateSpotData(
                                                activeSpot.data.id,
                                                property,
                                                value
                                            )
                                        }}
                                    />
                                    {isMediaCategoryFiltered && (
                                        <div className={'zr-my-4'}>
                                            <CategoryMediaSelect
                                                selectedCategoryIds={
                                                    activeSpot.data
                                                        .media_category_ids ||
                                                    []
                                                }
                                                onToggle={(categoryId) =>
                                                    handleToggleCategory(
                                                        categoryId
                                                    )
                                                }
                                                enableAddNewCategory={false}
                                            />
                                            <p
                                                className={cx(
                                                    styles[
                                                        'small-text-line-height'
                                                    ],
                                                    'zr-mt-2'
                                                )}
                                            >
                                                {t(
                                                    'active_spot_cart.labels.media_category_filtered_text'
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                    </React.Fragment>
                </>
            )}
            <>
                <Heading level={5}>
                    {t('active_spot_cart.labels.real_size')}
                </Heading>
                <div className={cx(styles['two-input-row'], 'zr-my-4')}>
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.width')}
                        property={'real_width'}
                        disabled={true}
                        value={realSizes?.width ?? ''}
                        onChange={() => {}}
                    />
                    <Input
                        type={'number'}
                        placeholder={t('active_spot_cart.labels.height')}
                        property={'real_height'}
                        disabled={true}
                        value={realSizes?.height ?? ''}
                        onChange={() => {}}
                    />
                </div>
            </>

            {(activeSpot.data.type === 'text' ||
                activeSpot.data.type === 'auto text') && (
                <>
                    <Heading level={4}>
                        {activeSpot.data.type === 'text'
                            ? t('active_spot_cart.single_text')
                            : t('active_spot_cart.auto_text')}
                    </Heading>
                    <div className={cx(styles['two-button-row'], 'zr-my-4')}>
                        <Checkbox
                            property={'multiline'}
                            value={activeSpot.data.multiline ?? false}
                            label={t('active_spot_cart.labels.multiline')}
                            onChange={(property, value) => {
                                updateSpotData(
                                    activeSpot.data.id,
                                    property,
                                    value
                                )
                            }}
                        />
                        <Input
                            type={'number'}
                            placeholder={t(
                                'active_spot_cart.labels.max_character'
                            )}
                            property={'max_character'}
                            value={activeSpot.data.max_character ?? '1000'}
                            onChange={(property, value) => {
                                updateSpotData(
                                    activeSpot.data.id,
                                    property,
                                    value
                                )
                            }}
                        />
                    </div>

                    {activeSpot.data.type === 'text' && (
                        <div className={'zr-my-4'}>
                            <Card classes={'zr-w-100'}>
                                <Checkbox
                                    property={'individual_font_size'}
                                    value={
                                        activeSpot.data.individual_font_size ??
                                        true
                                    }
                                    label={t(
                                        'active_spot_cart.labels.individual_font_size'
                                    )}
                                    onChange={(property, value) => {
                                        updateSpotData(
                                            activeSpot.data.id,
                                            property,
                                            value
                                        )
                                    }}
                                />
                                <p
                                    style={{
                                        fontSize: '0.75rem',
                                        lineHeight: '1.5',
                                    }}
                                >
                                    {t(
                                        'active_spot_cart.labels.individual_font_size_text'
                                    )}
                                </p>
                                {!(
                                    activeSpot.data.individual_font_size ?? true
                                ) && (
                                    <>
                                        <div
                                            style={{ position: 'relative' }}
                                            className={'zr-my-4'}
                                        >
                                            <Input
                                                type={'number'}
                                                placeholder={t(
                                                    'active_spot_cart.labels.font_size'
                                                )}
                                                property={'font_size'}
                                                value={
                                                    activeSpot.data.font_size ??
                                                    40
                                                }
                                                onChange={(property, value) => {
                                                    updateSpotData(
                                                        activeSpot.data.id,
                                                        property,
                                                        value
                                                    )
                                                    changeExampleText(
                                                        activeSpot.data.id,
                                                        {
                                                            fontSize:
                                                                parseInt(value),
                                                        }
                                                    )
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    right: '1rem',
                                                    top: '0.85rem',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    gap: '1rem',
                                                }}
                                            >
                                                <IconButton
                                                    icon={faMinus}
                                                    color={'black'}
                                                    onClick={() => {
                                                        updateSpotData(
                                                            activeSpot.data.id,
                                                            'font_size',
                                                            parseInt(
                                                                activeSpot.data
                                                                    .font_size ??
                                                                    '40'
                                                            ) - 1
                                                        )
                                                        changeExampleText(
                                                            activeSpot.data.id,
                                                            {
                                                                fontSize:
                                                                    parseInt(
                                                                        activeSpot
                                                                            .data
                                                                            .font_size ??
                                                                            '40'
                                                                    ) - 1,
                                                            }
                                                        )
                                                    }}
                                                />
                                                <IconButton
                                                    icon={faPlus}
                                                    color={'black'}
                                                    onClick={() => {
                                                        updateSpotData(
                                                            activeSpot.data.id,
                                                            'font_size',
                                                            parseInt(
                                                                activeSpot.data
                                                                    .font_size ??
                                                                    '40'
                                                            ) + 1
                                                        )
                                                        changeExampleText(
                                                            activeSpot.data.id,
                                                            {
                                                                fontSize:
                                                                    parseInt(
                                                                        activeSpot
                                                                            .data
                                                                            .font_size ??
                                                                            '40'
                                                                    ) + 1,
                                                            }
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <Input
                                            type={t(
                                                'active_spot_cart.labels.example_text'
                                            )}
                                            property={'example_text'}
                                            placeholder={t(
                                                'active_spot_cart.labels.example_text'
                                            )}
                                            value={getExampleText?.text ?? ''}
                                            onChange={(_, value) =>
                                                changeExampleText(
                                                    activeSpot.data.id,
                                                    { text: value }
                                                )
                                            }
                                        />
                                    </>
                                )}
                            </Card>
                        </div>
                    )}
                    <div className={'zr-my-4'}>
                        <Card classes={'zr-w-100'}>
                            <Checkbox
                                property={'individual_text_color'}
                                value={
                                    activeSpot.data.individual_text_color ??
                                    true
                                }
                                label={t(
                                    'active_spot_cart.labels.individual_text_color'
                                )}
                                onChange={(property, value) => {
                                    updateSpotData(
                                        activeSpot.data.id,
                                        property,
                                        value
                                    )
                                }}
                            />
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    lineHeight: '1.5',
                                }}
                            >
                                {t(
                                    'active_spot_cart.labels.individual_color_text'
                                )}
                            </p>
                            {!(
                                activeSpot.data.individual_text_color ?? true
                            ) && (
                                <CircleColorPicker
                                    classes={cx(styles['color-picker'])}
                                    property={'text_color'}
                                    label={t(
                                        'active_spot_cart.labels.text_color'
                                    )}
                                    value={activeSpot.data.text_color ?? '#000'}
                                    onChange={(property, value) => {
                                        updateSpotData(
                                            activeSpot.data.id,
                                            property,
                                            value
                                        )
                                        changeExampleText(activeSpot.data.id, {
                                            fill: value,
                                        })
                                    }}
                                />
                            )}
                        </Card>
                    </div>
                    <div className={'zr-my-4'}>
                        <Card classes={'zr-w-100'}>
                            <Checkbox
                                property={'individual_font_family'}
                                value={
                                    activeSpot.data.individual_font_family ??
                                    true
                                }
                                label={t(
                                    'active_spot_cart.labels.individual_font_family'
                                )}
                                onChange={(property, value) => {
                                    updateSpotData(
                                        activeSpot.data.id,
                                        property,
                                        value
                                    )
                                }}
                            />
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    lineHeight: '1.5',
                                }}
                            >
                                {t(
                                    'active_spot_cart.labels.individual_font_text'
                                )}
                            </p>
                            {!(
                                activeSpot.data.individual_font_family ?? true
                            ) && (
                                <MultiSelect
                                    options={fonts.map((f) => ({
                                        label: f.name,
                                        value: f.id + '',
                                    }))}
                                    onChange={(property, value, single) => {
                                        updateSpotData(
                                            activeSpot.data.id,
                                            property,
                                            value
                                        )
                                        changeExampleText(activeSpot.data.id, {
                                            fontFamily: single.label,
                                        })
                                    }}
                                    property={'font_families'}
                                    selected={
                                        activeSpot.data.font_families
                                            ? activeSpot.data.font_families.split(
                                                  ','
                                              )
                                            : []
                                    }
                                    placeholder={'Schriftart'}
                                    className={'zr-mt-2 zr-mb-4'}
                                />
                            )}
                        </Card>
                    </div>
                    {spots.filter(
                        (spot) => spot.data.sync_id === activeSpot.data.id
                    ).length === 0 && (
                        <Card classes={'zr-w-100'}>
                            <Heading classes={'zr-pb-2'} level={4}>
                                Synchronisation
                            </Heading>
                            <Select
                                options={getOptionsForSync}
                                onSelect={(property, value) => {
                                    updateSpotData(
                                        activeSpot.data.id,
                                        property,
                                        value
                                    )
                                }}
                                property={'sync_id'}
                                value={
                                    activeSpot.data.sync_id === 0 ||
                                    activeSpot.data.sync_id === null
                                        ? '0'
                                        : activeSpot.data.sync_id
                                }
                                placeholder={t(
                                    'active_spot_cart.labels.sync_with'
                                )}
                                searchable={false}
                            />
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    lineHeight: '1.5',
                                    marginTop: '1rem',
                                }}
                            >
                                {t(
                                    'active_spot_cart.labels.individual_sync_text'
                                )}
                            </p>
                        </Card>
                    )}
                </>
            )}
            {activeSpot.data.type === 'cutting mask' && (
                <>
                    <Heading level={4}>
                        {t('active_spot_cart.cutting_mask')}
                    </Heading>
                    <div className={cx(styles['two-input-row'], 'zr-my-4')}>
                        <Select
                            options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                (num) => {
                                    return {
                                        label: num.toString(),
                                        value: num.toString(),
                                    }
                                }
                            )}
                            onSelect={(property, value) =>
                                updateSpotData(
                                    activeSpot.data.id,
                                    property,
                                    value
                                )
                            }
                            property={'max_text_fields'}
                            value={activeSpot.data.max_text_fields ?? '10'}
                            placeholder={t(
                                'active_spot_cart.labels.max_text_fields'
                            )}
                            searchable={false}
                        />
                        <Select
                            options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                (num) => {
                                    return {
                                        label: num.toString(),
                                        value: num.toString(),
                                    }
                                }
                            )}
                            onSelect={(property, value) =>
                                updateSpotData(
                                    activeSpot.data.id,
                                    property,
                                    value
                                )
                            }
                            property={'max_image_fields'}
                            value={activeSpot.data.max_image_fields ?? '10'}
                            placeholder={t(
                                'active_spot_cart.labels.max_image_fields'
                            )}
                            searchable={false}
                        />
                    </div>
                    <div>
                        {activeSpot.data.max_image_fields > 0 && (
                            <div className={'zr-my-4'}>
                                <Card classes={'zr-w-100'}>
                                    <div className={'zr-mt-4'}>
                                        <Select
                                            options={getMediaOptions}
                                            onSelect={(_, value) =>
                                                handleMediaSelect(value)
                                            }
                                            property={'media_option'}
                                            value={mediaOptionValue()}
                                            placeholder={t(
                                                'active_spot_cart.labels.media_options'
                                            )}
                                            searchable={false}
                                        />
                                    </div>
                                    <p
                                        className={cx(
                                            styles['small-text-line-height']
                                        )}
                                    >
                                        {t(
                                            'active_spot_cart.labels.individual_media_text'
                                        )}
                                    </p>
                                    {'media_upload' in activeSpot.data &&
                                        activeSpot.data.media_upload && (
                                            <div className={'zr-my-4'}>
                                                <Checkbox
                                                    property={'grayscale'}
                                                    value={
                                                        activeSpot.data
                                                            .grayscale ?? false
                                                    }
                                                    label={t(
                                                        'active_spot_cart.labels.grayscale'
                                                    )}
                                                    onChange={(
                                                        property,
                                                        value
                                                    ) => {
                                                        updateSpotData(
                                                            activeSpot.data.id,
                                                            property,
                                                            value
                                                        )
                                                    }}
                                                />
                                                <p
                                                    className={cx(
                                                        styles[
                                                            'small-text-line-height'
                                                        ]
                                                    )}
                                                >
                                                    {t(
                                                        'active_spot_cart.labels.gray_scale_text'
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    {'media_library' in activeSpot.data &&
                                        activeSpot.data.media_library && (
                                            <div className={'zr-my-4'}>
                                                <Checkbox
                                                    property={
                                                        'media_category_filtered'
                                                    }
                                                    value={
                                                        activeSpot.data
                                                            .media_category_filtered ??
                                                        false
                                                    }
                                                    label={t(
                                                        'active_spot_cart.labels.media_category_filtered'
                                                    )}
                                                    onChange={(
                                                        property,
                                                        value
                                                    ) => {
                                                        setIsMediaCategoryFiltered(
                                                            value
                                                        )
                                                        updateSpotData(
                                                            activeSpot.data.id,
                                                            property,
                                                            value
                                                        )
                                                    }}
                                                />
                                                {isMediaCategoryFiltered && (
                                                    <div className={'zr-my-4'}>
                                                        <CategoryMediaSelect
                                                            selectedCategoryIds={
                                                                activeSpot.data
                                                                    .media_category_ids ||
                                                                []
                                                            }
                                                            onToggle={(
                                                                categoryId
                                                            ) =>
                                                                handleToggleCategory(
                                                                    categoryId
                                                                )
                                                            }
                                                            enableAddNewCategory={
                                                                false
                                                            }
                                                        />
                                                        <p
                                                            className={cx(
                                                                styles[
                                                                    'small-text-line-height'
                                                                ],
                                                                'zr-mt-2'
                                                            )}
                                                        >
                                                            {t(
                                                                'active_spot_cart.labels.media_category_filtered_text'
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </Card>
                            </div>
                        )}
                        {activeSpot.data.max_text_fields > 0 && (
                            <>
                                <div className={'zr-my-4'}>
                                    <Card classes={'zr-w-100'}>
                                        <Checkbox
                                            property={'individual_font_size'}
                                            value={
                                                activeSpot.data
                                                    .individual_font_size ??
                                                true
                                            }
                                            label={t(
                                                'active_spot_cart.labels.individual_font_size'
                                            )}
                                            onChange={(property, value) => {
                                                updateSpotData(
                                                    activeSpot.data.id,
                                                    property,
                                                    value
                                                )
                                            }}
                                        />
                                        <p
                                            className={cx(
                                                styles['small-text-line-height']
                                            )}
                                        >
                                            {t(
                                                'active_spot_cart.labels.individual_font_size_text'
                                            )}
                                        </p>
                                        {!(
                                            activeSpot.data
                                                .individual_font_size ?? true
                                        ) && (
                                            <>
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                    }}
                                                    className={'my-4'}
                                                >
                                                    <Input
                                                        type={'number'}
                                                        placeholder={t(
                                                            'active_spot_cart.labels.font_size'
                                                        )}
                                                        property={'font_size'}
                                                        value={
                                                            activeSpot.data
                                                                .font_size ?? 40
                                                        }
                                                        onChange={(
                                                            property,
                                                            value
                                                        ) => {
                                                            updateSpotData(
                                                                activeSpot.data
                                                                    .id,
                                                                property,
                                                                value
                                                            )
                                                            changeExampleText(
                                                                activeSpot.data
                                                                    .id,
                                                                {
                                                                    fontSize:
                                                                        parseInt(
                                                                            value
                                                                        ),
                                                                }
                                                            )
                                                        }}
                                                    />
                                                    <div
                                                        className={cx(
                                                            styles[
                                                                'icon-activespot'
                                                            ]
                                                        )}
                                                    >
                                                        <IconButton
                                                            icon={faMinus}
                                                            color={'black'}
                                                            onClick={() => {
                                                                updateSpotData(
                                                                    activeSpot
                                                                        .data
                                                                        .id,
                                                                    'font_size',
                                                                    parseInt(
                                                                        activeSpot
                                                                            .data
                                                                            .font_size ??
                                                                            '40'
                                                                    ) - 1
                                                                )
                                                                changeExampleText(
                                                                    activeSpot
                                                                        .data
                                                                        .id,
                                                                    {
                                                                        fontSize:
                                                                            parseInt(
                                                                                activeSpot
                                                                                    .data
                                                                                    .font_size ??
                                                                                    '40'
                                                                            ) -
                                                                            1,
                                                                    }
                                                                )
                                                            }}
                                                        />
                                                        <IconButton
                                                            icon={faPlus}
                                                            color={'black'}
                                                            onClick={() => {
                                                                updateSpotData(
                                                                    activeSpot
                                                                        .data
                                                                        .id,
                                                                    'font_size',
                                                                    parseInt(
                                                                        activeSpot
                                                                            .data
                                                                            .font_size ??
                                                                            '40'
                                                                    ) + 1
                                                                )
                                                                changeExampleText(
                                                                    activeSpot
                                                                        .data
                                                                        .id,
                                                                    {
                                                                        fontSize:
                                                                            parseInt(
                                                                                activeSpot
                                                                                    .data
                                                                                    .font_size ??
                                                                                    '40'
                                                                            ) +
                                                                            1,
                                                                    }
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <Input
                                                    type={t(
                                                        'active_spot_cart.labels.example_text'
                                                    )}
                                                    property={'example_text'}
                                                    placeholder={t(
                                                        'active_spot_cart.labels.example_text'
                                                    )}
                                                    value={
                                                        getExampleText?.text ??
                                                        ''
                                                    }
                                                    onChange={(_, value) =>
                                                        changeExampleText(
                                                            activeSpot.data.id,
                                                            { text: value }
                                                        )
                                                    }
                                                />
                                            </>
                                        )}
                                    </Card>
                                </div>
                                <Card classes={'zr-w-100'}>
                                    <Checkbox
                                        property={'individual_font_family'}
                                        value={
                                            activeSpot.data
                                                .individual_font_family ?? true
                                        }
                                        label={t(
                                            'active_spot_cart.labels.individual_font_family'
                                        )}
                                        onChange={(property, value) => {
                                            updateSpotData(
                                                activeSpot.data.id,
                                                property,
                                                value
                                            )
                                        }}
                                    />
                                    <p
                                        className={cx(
                                            styles['small-text-line-height']
                                        )}
                                    >
                                        {t(
                                            'active_spot_cart.labels.individual_font_text'
                                        )}
                                    </p>
                                    {!(
                                        activeSpot.data
                                            .individual_font_family ?? true
                                    ) && (
                                        <MultiSelect
                                            options={fonts.map((f) => ({
                                                label: f.name,
                                                value: f.id + '',
                                            }))}
                                            onChange={(
                                                property,
                                                value,
                                                single
                                            ) => {
                                                updateSpotData(
                                                    activeSpot.data.id,
                                                    property,
                                                    value
                                                )
                                                changeExampleText(
                                                    activeSpot.data.id,
                                                    { fontFamily: single.label }
                                                )
                                            }}
                                            property={'font_families'}
                                            selected={
                                                activeSpot.data.font_families
                                                    ? activeSpot.data.font_families.split(
                                                          ','
                                                      )
                                                    : []
                                            }
                                            placeholder={'Schriftart'}
                                            className={'zr-mt-2 zr-mb-4'}
                                        />
                                    )}
                                </Card>
                            </>
                        )}
                    </div>
                    {activeSpot.data.max_text_fields > 0 && (
                        <div className={'my-4'}>
                            <Card classes={'zr-w-100'}>
                                <Checkbox
                                    property={'individual_text_color'}
                                    value={
                                        activeSpot.data.individual_text_color ??
                                        true
                                    }
                                    label={t(
                                        'active_spot_cart.labels.individual_text_color'
                                    )}
                                    onChange={(property, value) => {
                                        updateSpotData(
                                            activeSpot.data.id,
                                            property,
                                            value
                                        )
                                    }}
                                />
                                <p
                                    className={cx(
                                        styles['small-text-line-height']
                                    )}
                                >
                                    {t(
                                        'active_spot_cart.labels.individual_color_text'
                                    )}
                                </p>
                                {!(
                                    activeSpot.data.individual_text_color ??
                                    true
                                ) && (
                                    <CircleColorPicker
                                        classes={cx(styles['color-picker'])}
                                        property={'text_color'}
                                        label={t(
                                            'active_spot_cart.labels.text_color'
                                        )}
                                        value={
                                            activeSpot.data.text_color ?? '#000'
                                        }
                                        onChange={(property, value) => {
                                            updateSpotData(
                                                activeSpot.data.id,
                                                property,
                                                value
                                            )
                                            changeExampleText(
                                                activeSpot.data.id,
                                                { fill: value }
                                            )
                                        }}
                                    />
                                )}
                            </Card>
                        </div>
                    )}
                </>
            )}
        </Card>
    )
}

export default ActiveSpotCard
